import { MatDialog } from '@angular/material/dialog';
import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  inject,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DecisionDto, LogDto, State, StateUtils } from '@coin/importer/dto/util';
import { Observable, Subscription } from 'rxjs';
import { environment } from '@coin/shared/core/util-environments';
import { SimpleTableComponent, SimpleTableHeader } from '../../components/simple-table/simple-table.component';
import { UploadPopupComponent } from '../../components/upload-popup/upload-popup.component';
import { LogsService } from '../../services/logs/logs.service';
import { ToastService } from '../../services/toast/toast.service';
import { FileTypeService } from '../../services/file-type/file-type.service';
import { SkipPreviousImportsPopupComponent } from '../../components/skip-previous-imports-popup/skip-previous-imports-popup.component';
import { TriggerImportRunPopupComponent } from '../../components/trigger-import-run-popup/trigger-import-run-popup.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SubHeaderComponent } from '../../components/sub-header/sub-header.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe, DecimalPipe, DatePipe } from '@angular/common';
import { FileSizePipe } from '../../pipes/file-size/file-size.pipe';
import { PercentagePipe } from '../../pipes/percentage/percentage.pipe';
import { FileNamePipe } from '../../pipes/file-name/file-name.pipe';
import { V2ButtonComponent } from '@coin/shared/core/ui-storybook';

@Component({
  selector: 'importer-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SubHeaderComponent,
    V2ButtonComponent,
    SimpleTableComponent,
    MatTooltip,
    MatIcon,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
    FileSizePipe,
    PercentagePipe,
    FileNamePipe
  ]
})
export class DashboardComponent implements OnInit {
  selectedTableService = inject(LogsService);
  private readonly router = inject(Router);
  private readonly _matDialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);
  private readonly logsService = inject(LogsService);
  private readonly toast = inject(ToastService);
  private readonly fileTypeService = inject(FileTypeService);

  @ViewChild(SimpleTableComponent) simpleTable: SimpleTableComponent;

  appliedFilters = {};
  public queryParameters$: Observable<Params> = this.route.queryParamMap;
  checkedItems = [];
  private readonly destroyRef = inject(DestroyRef);
  private resetSubription: Subscription;
  public isExpectedToChange = StateUtils.isExpectedToChange;
  public updaterIntervalInMs = environment.subscriptionIntervalInMs;
  public selectedTableHeaders: SimpleTableHeader[] = [
    {
      title: '',
      key: '',
      width: '50px'
    },
    {
      title: 'File Name',
      key: 'fileName',
      filterType: 'input',
      width: '3fr'
    },
    {
      title: 'Process Step',
      key: 'state',
      filterType: 'dropdown',
      values: Object.values(State),
      width: '3fr'
    },
    {
      title: 'Uploaded At',
      key: 'uploadedAt',
      filterType: 'date',
      width: '3fr'
    },
    {
      title: 'File Type',
      key: 'fileType',
      filterType: 'input',
      width: '2fr'
    },
    {
      title: 'File Size',
      key: 'uploadSizeInBytes',
      width: '2fr'
    },
    {
      title: 'Delta Size',
      width: '3fr'
    },
    {
      title: 'Invalid Datasets',
      width: '3fr'
    },
    {
      title: 'Decided By',
      width: '3fr'
    }
  ];

  // eslint-disable-next-line no-restricted-syntax
  async ngOnInit() {
    if (!this.fileTypeService.fileTypeSubject$.getValue()?.length) {
      await this.fileTypeService.loadFileTypesToSubject();
    }
    this.resetSubription = this.logsService.resetter$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isReset => {
      if (isReset) {
        this.simpleTable.fullReset();
      }
    });
  }

  loadSummary(element: LogDto) {
    this.router.navigate([`summary/${element.id}`]);
  }

  isStaging(element: LogDto): boolean {
    return element.state === State.Staging;
  }

  isPending(element: LogDto): boolean {
    return element.state === State.Pending;
  }

  isNotReadyState(element: LogDto): boolean {
    return this.isPending(element) && this.isStaging(element);
  }

  isPostStaging(element: LogDto): boolean {
    return element.state !== State.Pending && element.state !== State.Staging;
  }

  openJumpPopup(element: LogDto) {
    const popup = this._matDialog.open(SkipPreviousImportsPopupComponent, {
      disableClose: true,
      data: { element }
    });

    popup
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(reload => {
        if (reload) {
          this.simpleTable.triggerReload();
        }
      });
  }

  openUploadPopup(): void {
    this._matDialog.open(UploadPopupComponent, {
      disableClose: true
    });
  }

  openTriggerImportRunPopup(): void {
    const popup = this._matDialog.open(TriggerImportRunPopupComponent, {
      disableClose: true
    });

    popup
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(reload => {
        if (reload) {
          this.simpleTable.triggerReload();
        }
      });
  }

  openSettingsPage() {
    this.router.navigate(['settings']);
  }

  getProgressPercentage(message: string): number {
    const arr = message.split('/');
    const percent = Number.parseInt(arr[0]) * (100 / Number.parseInt(arr[1]));
    return Math.round(percent);
  }

  getNameFromDecision(decision: DecisionDto): string {
    if (decision?.decidedBy?.firstname && decision?.decidedBy?.lastname) {
      return `${decision.decidedBy.firstname} ${decision.decidedBy.lastname}`;
    }
    if (decision?.decidedBy?.lastname) {
      return `${decision.decidedBy.lastname}`;
    }
    return 'N/A';
  }
}
